import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { Subclass } from '../../types/class-data';
import { findClassById, findSubclass } from '../../utils/classDataLoader';
import ContentText from '../Common/ContentText';
import './SubclassSelection.css';

interface SubclassSelectionProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const SubclassSelection: React.FC<SubclassSelectionProps> = ({
  characterData,
  onDataChange,
}) => {
  const [subclasses, setSubclasses] = useState<Subclass[]>([]);
  const [selectedSubclass, setSelectedSubclass] = useState<Subclass | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [className, setClassName] = useState<string>('');

  useEffect(() => {
    const loadSubclasses = async () => {
      try {
        if (!characterData.class) {
          setError('No class selected');
          setLoading(false);
          return;
        }

        // Load class info and subclasses using the new loader
        const classInfo = await findClassById(characterData.class);
        
        if (!classInfo) {
          setError('Class not found');
          setLoading(false);
          return;
        }

        setClassName(classInfo.name);
        setSubclasses(classInfo.subclasses);
        
        // If character already has a subclass selected, find and set it
        if (characterData.subclass) {
          const found = await findSubclass(characterData.class, characterData.subclass);
          if (found) {
            setSelectedSubclass(found);
          }
        }
      } catch (err) {
        console.error('Failed to load subclasses:', err);
        setError('Failed to load subclass data');
      } finally {
        setLoading(false);
      }
    };

    loadSubclasses();
  }, [characterData.class, characterData.subclass]);

  const handleSubclassSelect = (subclass: Subclass) => {
    setSelectedSubclass(subclass);
    onDataChange({ subclass: subclass.id });
  };

  if (loading) {
    return <div className="subclass-selection__loading">Loading subclasses...</div>;
  }

  if (error) {
    return <div className="subclass-selection__error">{error}</div>;
  }

  return (
    <div className="subclass-selection">
      <p className="subclass-selection__description">
        Choose your {className} subclass. Each subclass offers different specializations and abilities
        that will shape your character's playstyle and capabilities.
      </p>

      <div className="subclass-selection__content">
        {/* Subclass List */}
        <div className="subclass-selection__list">
          <h3>Available {className} Subclasses</h3>
          <div className="subclass-grid">
            {subclasses.map((subclass) => (
              <div
                key={subclass.id}
                className={`subclass-card ${
                  selectedSubclass?.id === subclass.id ? 'subclass-card--selected' : ''
                }`}
                onClick={() => handleSubclassSelect(subclass)}
              >
                <div className="subclass-card__name">{subclass.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Subclass Details */}
        {selectedSubclass && (
          <div className="subclass-selection__details">
            <div className="subclass-details">
              <h3>{selectedSubclass.name}</h3>
              <ContentText 
                content={selectedSubclass.description}
                className="subclass-details__description"
              />

              <div className="subclass-details__info">
                {selectedSubclass.spellcastTrait && (
                  <div className="info-section">
                    <h4>Spellcasting</h4>
                    <p>This subclass uses <strong>{selectedSubclass.spellcastTrait}</strong> for spellcasting.</p>
                  </div>
                )}

                <div className="info-section">
                  <h4>Foundation Features</h4>
                  <div className="feature-item">
                    {Array.isArray(selectedSubclass.foundationFeature) && selectedSubclass.foundationFeature.map((feature, idx) => (
                      <div key={idx}>
                        <h5>{feature.name}</h5>
                        <ContentText content={feature.description} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="info-section">
                  <h4>Specialization Features</h4>
                  <div className="feature-item">
                    {Array.isArray(selectedSubclass.specializationFeature) && selectedSubclass.specializationFeature.map((feature, idx) => (
                      <div key={idx}>
                        <h5>{feature.name}</h5>
                        <ContentText content={feature.description} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="info-section">
                  <h4>Mastery Features</h4>
                  <div className="feature-item">
                    {Array.isArray(selectedSubclass.masteryFeature) && selectedSubclass.masteryFeature.map((feature, idx) => (
                      <div key={idx}>
                        <h5>{feature.name}</h5>
                        <ContentText content={feature.description} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubclassSelection;